import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FaEdit as EditIcon, FaEye as ViewIcon } from "react-icons/fa";
import { toast, ToastContainer } from 'react-toastify';
import { Icon, IconButton, Spinner } from '../styles/GlobalStyles';
import { FaTrash as DeleteIcon } from "react-icons/fa";
import { AppDispatch, RootState } from '../app/store';
import { useDispatch, useSelector } from 'react-redux';
import { Coupon, deleteCoupon, generateCoupons, getCouponsContent, resetDeleteWalletStatus, resetGenerateCouponsStatus, setCurrentPage } from '../features/couponsSlice';
import ReactPaginate from 'react-paginate';
import GenerateCouponsModal from '../components/generate_coupons_modal';
import { useNavigate } from 'react-router-dom';
import Modal from '../components/modal';
import ManageCouponBoxPage from './manage_coupon_box_page';
import useConfirmation from '../components/confirm_modal';

// Styled components for pagination
const MyPaginate = styled(ReactPaginate).attrs({
  activeClassName: "active",
})`
  margin-bottom: 2rem;
  display: flex;
  flex-direction: row;
  list-style-type: none;
  justify-content: center;
  padding: 0 5px;

  li a {
      border-radius: 7px;
      padding: 0.1rem 1rem;
      border: gray 1px solid;
      cursor: pointer;
  }
  li.previous a,
  li.next a,
  li.break a {
      border-color: transparent;
  }
  li.active a {
      background-color: #0366d6;
      border-color: transparent;
      color: white;
      min-width: 32px;
  }
  li.disabled a {
      color: grey;
  }
  li.disable,
  li.disabled a {
      cursor: default;
  }
`;

// Styled components
const Container = styled.div`
  padding: 20px;
  background-color: #f9f9f9;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 24px;
  color: #333;
`;

const NewBannerButton = styled.button`
  background-color: #28a745;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  &:hover {
    background-color: #218838;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #ccc;
`;

const Th = styled.th`
  padding: 12px 15px;
  background-color: #f1f1f1;
  text-align: left;
  color: #333;
`;

const Td = styled.td`
  padding: 12px 15px;
  text-align: left;
  color: #333;
  border-top: 1px solid #ccc;
`;


const CouponsPage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);

  const { confirm, ConfirmationModal } = useConfirmation();

  const {
    coupons,
    isNeedToRefresh,
    currentPage,
    totalItems,
    isGenerateCouponsInProgress,
    isDeleteCouponInprogress,
    deleteCouponId,
    deleteCouponStatus,
    generateCouponsStatus,
    generateCouponsError } = useSelector(
      (state: RootState) => state.coupons
    );


  const itemsPerPage = 20;

  const handlePageChange = (selectedPage: { selected: number }) => {
    dispatch(setCurrentPage(selectedPage.selected + 1));
  };

  useEffect(() => {
    dispatch(getCouponsContent());
  }, [currentPage, isNeedToRefresh, dispatch]);


  const handleDelete = async (coupon: Coupon) => {
    const isConfirmed = await confirm(`Going to delete this coupon ${coupon.code}. Do you want to proceed?`);
    if (!isConfirmed) return;
    dispatch(deleteCoupon(coupon.id));
  };

  useEffect(() => {
    if (deleteCouponStatus === 'succeeded') {
      toast.success('Coupon deleted');
      dispatch(resetDeleteWalletStatus());
    } else if (deleteCouponStatus === 'failed') {
      toast.error('Failed to delete Coupon');
      dispatch(resetDeleteWalletStatus());
    }
  }, [deleteCouponStatus, dispatch]);


  useEffect(() => {
    if (generateCouponsStatus === 'succeeded') {
      toast.success('Coupon added');
      dispatch(resetGenerateCouponsStatus());
    } else if (generateCouponsStatus === 'failed') {
      toast.error(`Failed to add wallet: ${generateCouponsError}`);
      dispatch(resetGenerateCouponsStatus());
    }
  }, [generateCouponsStatus, dispatch]);


  const handleGenerate = (num: number) => {
    setIsOpen(false);
    dispatch(generateCoupons(num));
  };

  const viewCouponbox = (boxId: string | null) => {
    if (boxId) {
      navigate(`/dashboard/boxes/details/${boxId}`);
    }
  };

  const openUpdateCouponModal = (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };


  return (
    <Container>
      <ToastContainer />
      <Header>
        <Title>Coupons</Title>
        <NewBannerButton onClick={() => setIsOpen(true)} disabled={isGenerateCouponsInProgress}>
          {isGenerateCouponsInProgress ? 'Generating...' : 'Generate Coupons'}
        </NewBannerButton>
      </Header>
      <Table>
        <thead>
          <tr>
            <Th>Coupon Code</Th>
            <Th>Created By</Th>
            <Th>Redeemed</Th>
            <Th>Redeemed By</Th>
            <Th>Type</Th>
            <Th>Box Name</Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {coupons.map((coupon) => (
            <tr key={coupon.id}>
              <Td>{coupon.code}</Td>
              <Td>{coupon.user_created_by?.email ?? 'N/A'}</Td>
              <Td>{coupon.redeemed ? 'Yes' : 'No'}</Td>
              <Td>{coupon.user_redeemed_by?.email ?? 'N/A'}</Td>
              <Td>{coupon.type}</Td>
              <Td>{coupon.box_details?.name ?? 'N/A'}</Td>
              <Td>
                <IconButton onClick={() => viewCouponbox(coupon.box_id)}>
                  <Icon><ViewIcon /></Icon>
                </IconButton>
                {!coupon.redeemed && (
                  <>
                    <IconButton onClick={() => openUpdateCouponModal(coupon)}>
                      <Icon><EditIcon /></Icon>
                    </IconButton>

                    <IconButton
                      disabled={isDeleteCouponInprogress}
                      onClick={() => handleDelete(coupon)}
                      paddingRight="0px"
                    >
                      {coupon.id === deleteCouponId && deleteCouponStatus === 'loading' ? (
                        <Spinner />
                      ) : (
                        <Icon><DeleteIcon /></Icon>
                      )}
                    </IconButton>
                  </>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      <GenerateCouponsModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onGenerate={handleGenerate}
      />
      <ConfirmationModal />
      <MyPaginate
        pageCount={Math.ceil(totalItems / itemsPerPage)}
        pageRangeDisplayed={3}
        marginPagesDisplayed={1}
        breakLabel="..."
        onPageChange={handlePageChange}
        containerClassName={"pagination"}
        activeClassName={"active"}
        forcePage={currentPage - 1}
      />
      {/* Modal for updating level box */}
      {isModalOpen && (
        <Modal isOpen={isModalOpen} onClose={() => { }} title="Update Level Box">
          <ManageCouponBoxPage isOpen={isModalOpen} coupon={selectedCoupon!} onClose={handleCloseModal} />
        </Modal>
      )}
    </Container>

  );
};

export default CouponsPage;



